import { IS_CLINICAL_TASKS_ENABLED } from '@/features/flags';
import { useIsSessionHandOffEnabled } from '@/features/flags/session-hand-off-flag';
import { useQueryJoinedOrganizations, useQueryJoinedTeams } from '@heidi/workspace/data';

/**
 * Hand off is only meaningful when there is someone to hand off TO: a user
 * outside any team or org structure has no colleagues, so every entry point
 * (V2 card row, shortcut, shortcuts helper, modal container) hides behind
 * this hook rather than the bare feature flag.
 *
 * The clinical-tasks flag is folded in here too — hand off creates a
 * clinical task the assignee must be able to see, so a single gate keeps
 * every surface (including the shortcuts helper) consistent with the live
 * chord. ROLLOUT: ramp `release-scribe-session-hand-off` only to cohorts
 * already inside the clinical-tasks rollout.
 *
 * APP-SIDE, and it stays app-side. This file is a PARTIAL
 * EXTRACTION from the retired clinical-tasks folder's `use-hand-off-
 * availability.ts`, which held both hooks: git paired that path with the
 * larger half, so the history is
 * `git log --follow packages/tasks/src/ui/hand-off-modal/use-hand-off-default-assignee.ts`.
 *
 * Both gates are PostHog-resolved legacy flags, which `@heidi/tasks/flags` is
 * not a surface for. The modal
 * itself moved behind the tasks door and takes the resolved boolean as its
 * `isAvailable` prop, supplied at `session-page-modals.tsx`. Its sibling
 * `useHandOffDefaultAssignee` travelled with the modal — it reads only
 * door-readable tasks flags and the packaged workspace queries, so nothing
 * held it here.
 */
export const useIsHandOffAvailable = (): boolean => {
  const isFlagEnabled = useIsSessionHandOffEnabled();
  // The packaged membership hooks expose no `enabled` seam, and adding one
  // would put packages/workspace (workspace-stream owned) in this PR's diff.
  // Both queries are shared react-query caches already populated by other
  // app surfaces, so calling them here adds no net fetch in practice.
  const { data: joinedTeams } = useQueryJoinedTeams();
  const { data: joinedOrgs } = useQueryJoinedOrganizations();

  const hasTeam = (joinedTeams?.joined_teams?.length ?? 0) > 0;
  const hasOrganization = (joinedOrgs?.organizations?.length ?? 0) > 0;

  return isFlagEnabled && IS_CLINICAL_TASKS_ENABLED() && (hasTeam || hasOrganization);
};
